import { useState } from 'react'
import { MenuTrigger } from 'react-aria-components'
import { ACCOUNT_PERMISSION } from '../../shared/constants'
import { Location, SavedView } from '../../shared/types'
import { useHasAccountPermission } from '../account-permissions'
import { useObjectListAll } from '../api/object'
import { useObjectPropertyListAll } from '../api/object-properties'
import { Button } from '../components/button'
import { Menu, MenuItem, Popover } from '../components/menu'
import { MultiSelect } from '../components/multi-select'
import { ObjectPropertyFilter } from '../components/object-property-filter'
import { CreateViewModal, UpdateViewModal } from '../components/saved-view-modals'
import { SidepanelContent, SidepanelFooter } from '../components/sidepanel'
import { quickDeepEqual } from '../query-deep-equal'
import { FilterWithProperties } from './types'

const locationsToOptions = (locations: Location[]) => [
	{ value: '', label: 'Nevyplněno' },
	...locations.map(({ id, name }) => ({ value: id, label: name })),
]

export function OverviewSidepanel({
	filter,
	view,
	onClose,
	onSubmit,
}: {
	filter: FilterWithProperties
	view?: SavedView
	onClose: VoidFunction
	onSubmit: (filter: FilterWithProperties) => void
}) {
	const [locationFrom, setLocationFrom] = useState(filter.locationFrom)
	const [locationTo, setLocationTo] = useState(filter.locationTo)
	const [properties, setProperties] = useState(filter.properties)
	const [savingModal, setSavingModal] = useState<null | 'new' | 'update'>(null)
	const newFilter: FilterWithProperties = {}
	if (locationFrom) newFilter.locationFrom = locationFrom
	if (locationTo) newFilter.locationTo = locationTo
	if (properties) newFilter.properties = properties

	const { data: locations = [] } = useObjectListAll('location', {
		select: locationsToOptions,
	})

	const { data: vehicleProperties = [] } = useObjectPropertyListAll('vehicle')

	const handleSubmit = (filter: FilterWithProperties) => {
		onClose()
		onSubmit(filter)
	}

	const isSelected = !quickDeepEqual(view?.filter, newFilter)
	const isUnchanged = quickDeepEqual(filter, newFilter)

	const canCreateViews = useHasAccountPermission(ACCOUNT_PERMISSION.ADD_DASHBOARD_VIEWS)
	const canEditViews = useHasAccountPermission(ACCOUNT_PERMISSION.EDIT_DASHBOARD_VIEWS)

	return (
		<>
			<SidepanelContent className="space-y-4">
				<h3>Rezervace</h3>
				<MultiSelect
					label="Pobočka z"
					options={locations}
					selectedKeys={locationFrom ?? []}
					onSelectionChange={setLocationFrom}
				/>
				<MultiSelect
					label="Pobočka do"
					options={locations}
					selectedKeys={locationTo ?? []}
					onSelectionChange={setLocationTo}
				/>
				<h3>Vozidla</h3>
				{vehicleProperties.map(({ id, ...vehicleProperty }) => (
					<ObjectPropertyFilter
						key={id}
						{...vehicleProperty}
						value={properties?.[vehicleProperty.identifier]}
						onChange={value => {
							setProperties(properties => ({
								...properties,
								[vehicleProperty.identifier]: value,
							}))
						}}
					/>
				))}
			</SidepanelContent>
			<SidepanelFooter>
				<div className="flex justify-between items-center gap-2 w-full">
					<Button
						variant="PRIMARY"
						isDisabled={isUnchanged}
						onPress={() => {
							handleSubmit(newFilter)
						}}
					>
						Filtrovat
					</Button>
					{isSelected ? (
						<>
							{view && (canCreateViews || canEditViews) ? (
								<MenuTrigger>
									<Button variant="SECONDARY">Uložit ...</Button>
									<Popover>
										<Menu>
											{canCreateViews ? (
												<MenuItem onAction={() => setSavingModal('new')}>
													Jako nový pohled
												</MenuItem>
											) : null}
											{canEditViews ? (
												<MenuItem onAction={() => setSavingModal('update')}>
													Změny pro existující
												</MenuItem>
											) : null}
										</Menu>
									</Popover>
								</MenuTrigger>
							) : canCreateViews ? (
								<Button variant="SECONDARY" onPress={() => setSavingModal('new')}>
									Uložit
								</Button>
							) : null}
							<Button
								variant="TEXT"
								size="TEXT"
								className="ml-auto"
								onPress={() => {
									handleSubmit({})
								}}
							>
								Zrušit
							</Button>
						</>
					) : null}
				</div>
			</SidepanelFooter>
			{savingModal === 'update' && (
				<UpdateViewModal
					object="dashboard"
					onClose={() => setSavingModal(null)}
					view={view as SavedView}
					filter={newFilter}
					onSubmit={handleSubmit}
				/>
			)}
			{savingModal === 'new' && (
				<CreateViewModal
					object="dashboard"
					onClose={() => setSavingModal(null)}
					filter={newFilter}
					onSubmit={handleSubmit}
				/>
			)}
		</>
	)
}
